import { Clock } from 'lucide-react';
import SelectableTile from '@carvana/ds-selectable-tile';
import dayjs from 'dayjs';
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import DatePicker from './DatePicker/Datepicker';
import { DateRange } from './DatePicker/utilities';
import styles from './TimeSlotPicker.module.css';
import {
  type TimeOption,
  type TimeWindowOption,
  type AvailableTimeGroups
} from './TimeSlotPicker.types';
import { useOrderSummaryContext } from '../../contexts/orderSummary';
import {
  useTransferScheduleContext,
  selectTimeSlotDate,
  selectTimeSlotTime,
  type TimeSlot,
  type TimeWindow
} from '../../contexts/transferSchedule';
import {
  formatDateForTimeHeader,
  formatDateShort,
  isSameDay,
  parseDate
} from '../../utils/dateFormatters';
import { getTimeZoneAbbreviation } from '../../utils/timezoneHelpers';

const TimeSlotPicker: React.FC = () => {
  const { state, transferScheduleDispatch } = useTransferScheduleContext();
  const { timeSlots, timeWindows, isFlexibleDelivery } = state;
  const {
    state: {
      orderSummary: { transfer }
    }
  } = useOrderSummaryContext();

  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [availableTimes, setAvailableTimes] = useState<(TimeOption | TimeWindowOption)[]>([]);
  const [month, setMonth] = useState<number>(new Date().getMonth());
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const timeZoneAbbreviation = useMemo(() => getTimeZoneAbbreviation(transfer), [transfer]);
  const firstAvailableTimeWindow: TimeWindow | undefined = useMemo(
    () => (timeWindows.length > 0 ? timeWindows[0] : undefined),
    [timeWindows]
  );
  const lastAvailableTimeWindow: TimeWindow | undefined = useMemo(
    () => (timeWindows.length > 0 ? [...timeWindows].reverse()[0] : undefined),
    [timeWindows]
  );
  const firstAvailableTimeSlot: TimeSlot | undefined = useMemo(
    () => (timeSlots.length > 0 ? timeSlots[0] : undefined),
    [timeSlots]
  );
  const lastAvailableTimeSlot: TimeSlot | undefined = useMemo(
    () => (timeSlots.length > 0 ? [...timeSlots].reverse()[0] : undefined),
    [timeSlots]
  );
  const firstAvailableDate: string | undefined = useMemo(
    () =>
      isFlexibleDelivery ? firstAvailableTimeWindow?.isoDate : firstAvailableTimeSlot?.isoDate,
    [isFlexibleDelivery, firstAvailableTimeWindow?.isoDate, firstAvailableTimeSlot?.isoDate]
  );
  const lastAvailableDate: string | undefined = useMemo(
    () => (isFlexibleDelivery ? lastAvailableTimeWindow?.isoDate : lastAvailableTimeSlot?.isoDate),
    [isFlexibleDelivery, lastAvailableTimeWindow?.isoDate, lastAvailableTimeSlot?.isoDate]
  );

  const selectedDateForDatePicker = useMemo(() => {
    if (!selectedDate) return undefined;
    const date = parseDate(selectedDate);
    return { start: date, end: date };
  }, [selectedDate]);

  const availableTimeGroups = useMemo((): AvailableTimeGroups | undefined => {
    if (isFlexibleDelivery) return undefined;

    const morning = (availableTimes as TimeOption[]).filter((slot: TimeOption) =>
      slot.time.endsWith('a.m.')
    );

    const afternoon = (availableTimes as TimeOption[]).filter((slot: TimeOption) => {
      if (!slot.time.endsWith('p.m.')) return false;
      const hour = Number.parseInt(slot.time);
      return hour < 5 || hour == 12;
    });

    const evening = (availableTimes as TimeOption[]).filter((slot: TimeOption) => {
      if (!slot.time.endsWith('p.m.')) return false;
      const hour = Number.parseInt(slot.time);
      return hour >= 5 && hour < 12;
    });

    return { morning, afternoon, evening };
  }, [availableTimes, isFlexibleDelivery]);

  const handleDateChange = useCallback<(date: DateRange) => void>(
    ({ start }): void => {
      setSelectedDate(formatDateShort(start));

      if (isFlexibleDelivery) {
        const selectedTimeWindow = timeWindows.find((timeWindow: TimeWindow) =>
          isSameDay(start, parseDate(timeWindow.isoDate))
        );
        if (selectedTimeWindow) {
          const { date, times } = selectedTimeWindow;
          setSelectedTime('');
          setAvailableTimes(
            times.map(time => ({
              time: time.time,
              endRange: time.endRange,
              activityType: time.activityType,
              optionId: time.optionId,
              routeId: time.routeId,
              utcStartTime: time.utcStartTime
            }))
          );
          selectTimeSlotDate(transferScheduleDispatch, date);
        }
      } else {
        const selectedTimeSlot = timeSlots.find((timeSlot: TimeSlot) =>
          isSameDay(start, parseDate(timeSlot.isoDate))
        );
        if (selectedTimeSlot) {
          const { date, times } = selectedTimeSlot;
          setSelectedTime('');
          setAvailableTimes(
            times.map(time => ({
              time: time.time,
              activityType: time.activityType,
              optionId: time.optionId,
              routeId: time.routeId,
              utcStartTime: time.utcStartTime,
              endRange: time.endRange
            }))
          );
          selectTimeSlotDate(transferScheduleDispatch, date);
        }
      }
    },
    [isFlexibleDelivery, timeSlots, timeWindows, transferScheduleDispatch]
  );

  const handleTimeChange = useCallback(
    (selectedIndex: number, timeSelected: string): void => {
      const timeSlotData = availableTimes[selectedIndex];
      setSelectedTime(timeSelected);
      selectTimeSlotTime(transferScheduleDispatch, {
        time: timeSlotData.time,
        activityType: timeSlotData.activityType ?? '',
        optionId: timeSlotData.optionId ?? '',
        routeId: timeSlotData.routeId ?? '',
        utcStartTime: timeSlotData.utcStartTime ?? 0,
        endRange: timeSlotData.endRange
      });
    },
    [availableTimes, transferScheduleDispatch]
  );

  const handleSlotChange = useCallback(
    (key: string, index: number, timeSelected: string): void => {
      if (availableTimeGroups) {
        const timeSlotData = availableTimeGroups[key as keyof AvailableTimeGroups][index];
        setSelectedTime(timeSelected);
        selectTimeSlotTime(transferScheduleDispatch, {
          time: timeSlotData.time,
          activityType: timeSlotData.activityType ?? '',
          optionId: timeSlotData.optionId ?? '',
          routeId: timeSlotData.routeId ?? '',
          utcStartTime: timeSlotData.utcStartTime ?? 0,
          endRange: timeSlotData.endRange
        });
      }
    },
    [availableTimeGroups, transferScheduleDispatch]
  );

  const disabledDates = useMemo(() => {
    const today = new Date();
    const blockedDates = [];
    const startDate = new Date(today.getFullYear(), today.getMonth(), 1);
    const endDate = new Date(today.getFullYear() + 1, today.getMonth(), 0);

    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      const isBlocked = isFlexibleDelivery
        ? !timeWindows.some((timeWindow: TimeWindow) => isSameDay(d, parseDate(timeWindow.isoDate)))
        : !timeSlots.some((timeSlot: TimeSlot) => isSameDay(d, parseDate(timeSlot.isoDate)));

      if (isBlocked) {
        blockedDates.push(new Date(d));
      }
    }

    return blockedDates;
  }, [isFlexibleDelivery, timeWindows, timeSlots]);

  useEffect(() => {
    if (firstAvailableDate) {
      const parsedDate = parseDate(firstAvailableDate);
      const dateRange: DateRange = {
        start: parsedDate,
        end: parsedDate
      };

      handleDateChange(dateRange);
      setMonth(parsedDate.getMonth());
      setYear(parsedDate.getFullYear());
    }
  }, [firstAvailableDate, handleDateChange]);

  return (
    <div className={styles.container}>
      <div className={styles.dayPickerWrapper}>
        <DatePicker
          month={month}
          year={year}
          selected={selectedDateForDatePicker}
          disableDatesBefore={
            firstAvailableDate ? dayjs(firstAvailableDate).startOf('day').toDate() : new Date()
          }
          disableDatesAfter={
            lastAvailableDate ? dayjs(lastAvailableDate).startOf('day').toDate() : undefined
          }
          disableSpecificDates={disabledDates}
          onChange={handleDateChange}
          onMonthChange={(newMonth, newYear) => {
            setMonth(newMonth);
            setYear(newYear);
          }}
          showHeader={true}
          header="Select date"
        />
      </div>

      {isFlexibleDelivery ? (
        <div className={styles.timeWrapper}>
          <div className={styles.timesTitles}>
            <Clock />
            {selectedDate
              ? `Select a time for ${formatDateForTimeHeader(selectedDate)}${timeZoneAbbreviation ? ` (${timeZoneAbbreviation})` : ''}`
              : `Select time${timeZoneAbbreviation ? ` (${timeZoneAbbreviation})` : ''}`}
          </div>
          <div className={styles.selectTimeWindowWrapper}>
            {(availableTimes as TimeWindowOption[]).map((window: TimeWindowOption, key: number) => (
              <SelectableTile
                key={key}
                variant="simple-select"
                headline={`${window.time} - ${window.endRange}`}
                headlineSize="small"
                value={window.time}
                selected={window.time === selectedTime}
                onClick={() => {
                  handleTimeChange(key, window.time);
                }}
                data-analytics-id={`timewindow-btn-${window.optionId ?? key}`}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className={styles.timeWrapper}>
          <div className={styles.timesTitles}>
            <Clock />
            {selectedDate
              ? `Select a time for ${formatDateForTimeHeader(selectedDate)}${timeZoneAbbreviation ? ` (${timeZoneAbbreviation})` : ''}`
              : `Select time${timeZoneAbbreviation ? ` (${timeZoneAbbreviation})` : ''}`}
          </div>
          <div className={styles.selectTimeWrapper}>
            {availableTimeGroups &&
              Object.entries(availableTimeGroups).map(([title, slots]: [string, TimeOption[]]) => {
                if (slots.length === 0) return null;
                return (
                  <React.Fragment key={title}>
                    <span className={styles.timeGroupTitle}>{title}</span>
                    <div className={styles.selectTimeLayout}>
                      {slots.map((slot: TimeOption, key: number) => (
                        <SelectableTile
                          key={slot.optionId ?? key}
                          variant="simple-select"
                          headline={slot.time}
                          headlineSize="small"
                          value={slot.time}
                          selected={slot.time === selectedTime}
                          onClick={() => {
                            handleSlotChange(title, key, slot.time);
                          }}
                          data-analytics-id={`timeslot-btn-${slot.optionId ?? key}`}
                        />
                      ))}
                    </div>
                  </React.Fragment>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
};

export default TimeSlotPicker;
